import React, { useState, useCallback, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileDown, FileSpreadsheet, Mail, Copy, PackageCheck, Loader2 } from "lucide-react";

const OWNERS = ["Client", "Implementation Partner", "ComplyX", "Shared"];
const TYPES = [["evidence", "Evidence"], ["action", "Action / Task"], ["question", "Question"]];
const PRIORITIES = ["Critical", "High", "Medium", "Low"];
const EVIDENCE_STATUSES = ["Not Requested", "Requested", "Received", "Under Review", "Accepted", "Rejected", "Expired", "Replaced"];
const EVIDENCE_TYPES = ["Screenshot", "Configuration export", "User list", "Device inventory", "Policy", "Procedure", "Training report", "Ticket", "Approval", "Log", "Scan report", "Diagram", "Interview confirmation", "Observation", "Implementer attestation", "Other"];

export function RequestPackageDialog({ open, onOpenChange, customerId = "", customers = [], caps }) {
  const [packageCustomerId, setPackageCustomerId] = useState(customerId);
  const [owner, setOwner] = useState("Client");
  const [types, setTypes] = useState({ evidence: true, action: false, question: false });
  const [openOnly, setOpenOnly] = useState(true);
  const [priorities, setPriorities] = useState({});
  const [statuses, setStatuses] = useState({});
  const [evidenceType, setEvidenceType] = useState("all");
  const [family, setFamily] = useState("");
  const [control, setControl] = useState("");
  const [message, setMessage] = useState("");
  const [items, setItems] = useState([]);
  const [header, setHeader] = useState(null);
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [emailDraft, setEmailDraft] = useState(null);

  const buildBody = useCallback((withRefs) => ({
    customer_id: packageCustomerId,
    owner_group: owner,
    request_types: Object.keys(types).filter((k) => types[k]),
    priorities: Object.keys(priorities).filter((k) => priorities[k]),
    statuses: Object.keys(statuses).filter((k) => statuses[k]),
    evidence_types: evidenceType === "all" ? [] : [evidenceType],
    families: family.trim() ? [family.trim()] : [],
    control_id: control.trim(),
    open_only: openOnly,
    message,
    ...(withRefs ? { selected_refs: Object.keys(selected).filter((r) => selected[r]) } : {}),
  }), [packageCustomerId, owner, types, priorities, statuses, evidenceType, family, control, openOnly, message, selected]);

  useEffect(() => {
    if (open) {
      setPackageCustomerId(customerId);
      setItems([]); setHeader(null); setSummary(null); setSelected({}); setEmailDraft(null);
    }
  }, [open, customerId]);

  const preview = async () => {
    if (!packageCustomerId) { toast.error("Select one customer before previewing a package."); return; }
    setLoading(true); setEmailDraft(null);
    try {
      const r = await api.post("/recon/request-package/preview", buildBody(false));
      setItems(r.data.items); setHeader(r.data.header); setSummary(r.data.summary);
      setSelected(Object.fromEntries(r.data.items.map((i) => [i.ref, true])));
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setLoading(false); }
  };

  const selectedRefs = Object.keys(selected).filter((r) => selected[r]);

  const doExport = async (fmt) => {
    if (!selectedRefs.length) { toast.error("Select at least one item."); return; }
    try {
      const res = await api.post(`/recon/request-package/export?fmt=${fmt}`, buildBody(true), { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `Request_Package.${fmt === "excel" ? "xlsx" : fmt}`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} exported (read-only).`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed"); }
  };

  const genEmail = async () => {
    if (!selectedRefs.length) { toast.error("Select at least one item."); return; }
    try {
      const r = await api.post("/recon/request-package/email-draft", buildBody(true));
      setEmailDraft(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const copyEmail = () => {
    const txt = `Subject: ${emailDraft.subject}\n\n${emailDraft.body}`;
    navigator.clipboard.writeText(txt).then(() => toast.success("Email draft copied to clipboard."));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="request-package-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-navy flex items-center gap-2"><PackageCheck className="h-5 w-5 text-gold-dark" /> Generate Request Package</DialogTitle>
          <DialogDescription>Package canonical Evidence Requests for one customer. Actions and questions are optional additions. Exporting is read-only — nothing is sent or changed.</DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-3 gap-3 items-end">
          <div><Label className="text-xs">Customer</Label>
            <Select value={packageCustomerId} onValueChange={setPackageCustomerId}>
              <SelectTrigger data-testid="pkg-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent>{customers.map((customer) => <SelectItem key={customer.id} value={customer.id}>{customer.company_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Recipient / Owner</Label>
            <Select value={owner} onValueChange={setOwner}>
              <SelectTrigger data-testid="pkg-owner"><SelectValue /></SelectTrigger>
              <SelectContent>{OWNERS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Request Types</Label>
            <div className="flex gap-3 mt-2">
              {TYPES.map(([k, lbl]) => (
                <label key={k} className="flex items-center gap-1.5 text-xs" data-testid={`pkg-type-${k}`}>
                  <Checkbox checked={!!types[k]} onCheckedChange={(v) => setTypes((t) => ({ ...t, [k]: !!v }))} /> {lbl}
                </label>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-1.5 text-xs" data-testid="pkg-open-only">
            <Checkbox checked={openOnly} onCheckedChange={(v) => setOpenOnly(!!v)} /> Open items only
          </label>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div><Label className="text-xs">Request type</Label>
            <Select value={evidenceType} onValueChange={setEvidenceType}><SelectTrigger data-testid="pkg-evidence-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All evidence request types</SelectItem>{EVIDENCE_TYPES.map((type) => <SelectItem key={type} value={type}>{type}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label className="text-xs">Family</Label><Input value={family} onChange={(e) => setFamily(e.target.value)} placeholder="e.g. 3.4" data-testid="pkg-family" /></div>
          <div><Label className="text-xs">Control</Label><Input value={control} onChange={(e) => setControl(e.target.value)} placeholder="e.g. 3.4.7" data-testid="pkg-control" /></div>
          <div><Label className="text-xs">Evidence status</Label>
            <Select value={Object.keys(statuses).find((status) => statuses[status]) || "all"} onValueChange={(value) => setStatuses(value === "all" ? {} : { [value]: true })}><SelectTrigger data-testid="pkg-status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All statuses</SelectItem>{EVIDENCE_STATUSES.map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <Label className="text-xs">Priority filter:</Label>
          {PRIORITIES.map((p) => (
            <label key={p} className="flex items-center gap-1 text-xs">
              <Checkbox checked={!!priorities[p]} onCheckedChange={(v) => setPriorities((s) => ({ ...s, [p]: !!v }))} /> {p}
            </label>
          ))}
          <Button size="sm" onClick={preview} disabled={loading} className="bg-navy text-white hover:bg-navy/90 ml-auto" data-testid="pkg-preview-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Preview Package
          </Button>
        </div>

        {header && summary && (
          <div className="rounded-md border border-border bg-card p-4 space-y-3" data-testid="pkg-preview">
            <div>
              <div className="font-display text-navy font-bold">{header.customer_name}</div>
              <div className="text-xs text-muted-foreground">{header.cmmc_level} · {header.package_title} · Generated {header.generated_date} · Prepared by {header.prepared_by}</div>
            </div>
            <div className="flex flex-wrap gap-2 text-xs" data-testid="pkg-summary">
              {[["Total", summary.total], ["Evidence", summary.evidence], ["Actions", summary.actions], ["Questions", summary.questions], ["High-Priority", summary.high_priority]].map(([k, v]) => (
                <span key={k} className="px-2 py-1 rounded bg-slate-100 text-navy"><b>{v}</b> {k}</span>
              ))}
            </div>
            <div><Label className="text-xs">Package message (optional)</Label>
              <Textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Short note included on the package…" data-testid="pkg-message" /></div>

            <div className="rounded border border-border overflow-hidden max-h-[40vh] overflow-y-auto">
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-50 border-b text-left">
                  {["", "Family", "Control", "Objectives", "Requirement", "Request Type", "Request", "Priority", "Requested From", "Due", "Status"].map((h) => <th key={h} className="px-2 py-1.5 font-semibold text-navy">{h}</th>)}
                </tr></thead>
                <tbody>
                  {items.length === 0 ? <tr><td colSpan={11} className="px-2 py-6 text-center text-muted-foreground">No outstanding items match these filters.</td></tr> :
                    items.map((i) => (
                      <tr key={i.ref} className="border-b border-border/60 last:border-0" data-testid="pkg-item">
                        <td className="px-2 py-1.5"><Checkbox checked={!!selected[i.ref]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [i.ref]: !!v }))} data-testid={`pkg-item-check-${i.ref}`} /></td>
                        <td className="px-2 py-1.5">{i.family || "—"}</td>
                        <td className="px-2 py-1.5 font-mono text-navy">{i.control || "—"}</td>
                        <td className="px-2 py-1.5">{i.objectives || "—"}</td>
                        <td className="px-2 py-1.5 max-w-[240px]">{i.requirement || "—"}</td>
                        <td className="px-2 py-1.5">{i.evidence_type || i.type}</td>
                        <td className="px-2 py-1.5 max-w-[280px]">{i.request}</td>
                        <td className="px-2 py-1.5">{i.priority}</td>
                        <td className="px-2 py-1.5">{i.responsible_party || "—"}</td>
                        <td className="px-2 py-1.5">{i.due_date || "—"}</td>
                        <td className="px-2 py-1.5">{i.status}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap gap-2 pt-1">
              {caps?.export && <Button size="sm" variant="outline" onClick={() => doExport("pdf")} data-testid="pkg-export-pdf"><FileDown className="h-4 w-4 mr-1.5" /> Export PDF</Button>}
              {caps?.export && <Button size="sm" variant="outline" onClick={() => doExport("excel")} data-testid="pkg-export-xlsx"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Export Excel</Button>}
              <Button size="sm" variant="outline" onClick={genEmail} data-testid="pkg-email-draft"><Mail className="h-4 w-4 mr-1.5" /> Copy Email Draft</Button>
              <span className="text-[11px] text-muted-foreground self-center ml-auto">{selectedRefs.length} selected</span>
            </div>

            {emailDraft && (
              <div className="rounded border border-gold/40 bg-gold/5 p-3 space-y-2" data-testid="pkg-email-preview">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-navy">Email Draft (copy — not sent)</span>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={copyEmail} data-testid="pkg-email-copy"><Copy className="h-3.5 w-3.5 mr-1" /> Copy</Button>
                </div>
                <div className="text-xs font-semibold text-navy">Subject: {emailDraft.subject}</div>
                <pre className="text-[11px] whitespace-pre-wrap text-slate-700 font-sans">{emailDraft.body}</pre>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
